import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
    AppState,
    useAuth0,
    Auth0Provider,
    LogoutOptions,
    PopupLoginOptions,
} from '@auth0/auth0-react';

import {
    clearTokenCache,
    registerTokenProvider,
    registerAuthErrorHandler,
    registerAgencyBlockedHandler,
} from 'src/utils/axios';

import { AUTH0_API } from 'src/config-global';

import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

type Props = {
    children: React.ReactNode;
};

function AuthProviderWrapper({ children }: Props) {
    const {
        isAuthenticated,
        user,
        isLoading,
        loginWithRedirect,
        loginWithPopup,
        logout,
        getAccessTokenSilently,
    } = useAuth0();

    useEffect(() => {
        registerAuthErrorHandler(() => {
            clearTokenCache();
            const returnTo = `${window.location.pathname}${window.location.search}`;
            loginWithRedirect?.({ appState: { returnTo } }).catch((err) => {
                console.error('Failed to redirect to login:', err);
            });
        });
        return () => registerAuthErrorHandler(null);
    }, [loginWithRedirect]);

    // Make the Auth0 token available to the axios request interceptor, which
    // attaches it to every request — so resource calls no longer thread it.
    useEffect(() => {
        registerTokenProvider(getAccessTokenSilently);
        return () => registerTokenProvider(null);
    }, [getAccessTokenSilently]);

    // Agency suspended/retired (ADR-0003 §5): the backend 403s every request. Tell the user why with a
    // persistent message, then sign them out — otherwise the app just appears broken. The handler is
    // latched in axios, so this fires once even though many requests fail.
    useEffect(() => {
        registerAgencyBlockedHandler((message) => {
            enqueueSnackbar(message || 'Your agency is no longer active. Please contact support.', {
                variant: 'error',
                persist: true,
            });
            clearTokenCache();
            // Give the user a few seconds to read the message before redirecting to Auth0 logout.
            setTimeout(() => {
                logout?.({ logoutParams: { returnTo: window.location.origin } });
            }, 4000);
        });
        return () => registerAgencyBlockedHandler(null);
    }, [logout]);

    const [popupClick, setPopupClick] = useState(true);

    // LOGIN
    const handleLoginWithPopup = useCallback(
        async (options?: PopupLoginOptions) => {
            loginWithPopup?.(options);
            setPopupClick(false);
        },
        [loginWithPopup]
    );

    // LOGOUT
    const handleLogout = useCallback(
        async (options?: LogoutOptions) => {
            logout?.(options);
        },
        [logout]
    );

    // ----------------------------------------------------------------------

    const checkAuthenticated = isAuthenticated ? 'authenticated' : 'unauthenticated';

    const status = popupClick && isLoading ? 'loading' : checkAuthenticated;

    const memoizedValue = useMemo(
        () => ({
            user: {
                ...user,
                displayName: user?.name,
                photoURL: user?.picture,
                role: user?.['https://miami-ship/roles'] || 'admin',
            },
            method: 'auth0',
            loading: status === 'loading',
            authenticated: status === 'authenticated',
            unauthenticated: status === 'unauthenticated',
            //
            loginWithRedirect,
            loginWithPopup: handleLoginWithPopup,
            logout: handleLogout,
            logoutUrl: '/auth/login',
        }),
        [handleLoginWithPopup, handleLogout, loginWithRedirect, status, user]
    );

    return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

// ----------------------------------------------------------------------

export const AuthProvider = ({ children }: Props) => {
    const domain = AUTH0_API.domain ?? '';

    const clientId = AUTH0_API.clientId ?? '';

    const redirectUri = AUTH0_API.callbackUrl ?? '';

    const onRedirectCallback = useCallback((appState?: AppState) => {
        window.location.replace(appState?.returnTo || window.location.pathname);
    }, []);

    if (!(domain && clientId && redirectUri)) {
        return null;
    }

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            authorizationParams={{
                redirect_uri: redirectUri,
                audience: AUTH0_API.audience,
                scope: "openid profile email offline_access read:client_grants"
            }}
            onRedirectCallback={onRedirectCallback}
            cacheLocation="localstorage"
            useRefreshTokens

        >
            <AuthProviderWrapper>{children}</AuthProviderWrapper>
        </Auth0Provider>
    );
};
